import * as zod from 'zod';

import { StepKind } from '@sb/routine-runner';

export interface StepSummary {
  id: string;
  stepKind: StepKind;
  /** Default description (this can be overwritten by each robot). */
  description?: string;
  substepsMinimized?: boolean;
  suppressed?: boolean;
  /** Nested steps */
  steps?: StepSummary[];
}

export const StepSummary: zod.ZodSchema<StepSummary> = zod.lazy(() =>
  zod.object({
    description: zod.string().optional(),
    substepsMinimized: zod.boolean().optional(),
    suppressed: zod.boolean().optional(),
    id: zod.string(),
    stepKind: StepKind,
    steps: zod.array(StepSummary).optional(),
  }),
);

// Recursively count every step in the tree, including children nested via
// `step.steps`. Shared between the routine-list UI (default description) and
// the agent's routine summary surface.
export function countSteps(steps: StepSummary[] | undefined): number {
  if (!steps) return 0;

  let count = 0;

  for (const step of steps) {
    count += 1 + countSteps(step.steps);
  }

  return count;
}

// User-visible step numbering: a flat depth-first pre-order counter
// over the whole tree, starting at 1, counting every step (including
// suppressed ones). This is the same numbering the routine editor
// displays (see `convertStepSummary` in @sb/remote-control — kept in
// lock-step by libs/remote-control/util/convertStepSummary.spec.ts).
export function computeStepPositions(
  steps: StepSummary[] | undefined,
): Record<string, number> {
  const positions: Record<string, number> = {};
  let position = 0;

  const walk = (substeps: StepSummary[] | undefined): void => {
    if (!substeps) return;

    for (const step of substeps) {
      position += 1;
      positions[step.id] = position;
      walk(step.steps);
    }
  };

  walk(steps);

  return positions;
}
